import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Film, Clapperboard, Loader2, Check, ArrowLeft, ArrowRight, Sparkles,
  Languages, Info, AlertTriangle, ListTree, Server, RotateCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOVIE_FLAGS = [
  { key: "legendado", label: "Identificar conteúdo legendado",
    desc: "Só funciona com a função Ler TMDB. Adiciona [L] no final do nome quando identificado como legendado." },
  { key: "uhd_4k", label: "Identificar conteúdo 4K",
    desc: "Só funciona com a função Ler TMDB. Adiciona [4K] no final do nome quando identificado como 4K." },
  { key: "remove_year", label: "Remover ano do título do filme",
    desc: "Remove o ano que aparece junto ao nome do filme." },
];

const Switch = ({ on, onToggle, testid }) => (
  <button type="button" data-testid={testid} onClick={onToggle}
    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-green-600" : "bg-slate-300"}`}>
    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

const ToggleField = ({ label, on, onToggle, testid }) => (
  <div className="flex items-center gap-2">
    <Switch on={on} onToggle={onToggle} testid={testid} />
    <span className="text-xs font-medium text-slate-600">{label}</span>
  </div>
);

const CheckOption = ({ opt, checked, onToggle, testid }) => (
  <button type="button" data-testid={testid} onClick={onToggle}
    className="flex w-full items-start gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-green-300">
    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 ${checked ? "border-green-600 bg-green-600 text-white" : "border-slate-300"}`}>
      {checked && <Check className="h-3.5 w-3.5" />}
    </span>
    <span>
      <span className="block text-sm font-medium text-slate-800">{opt.label}</span>
      <span className="mt-0.5 block text-xs text-slate-500">{opt.desc}</span>
    </span>
  </button>
);

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
const STOP = new Set(["filmes", "filme", "series", "serie", "movies", "movie", "tv", "canais", "vod", "de", "e", "the", "hd", "fhd", "sd", "uhd"]);
const meaningful = (s) => norm(s).split(" ").filter((w) => w && !STOP.has(w));
const suggestCat = (m3uName, list) => {
  const at = meaningful(m3uName);
  if (!at.length) return null;
  const aset = new Set(at), aStr = at.join(" ");
  let best = null, bestScore = 0;
  for (const c of list || []) {
    const bt = meaningful(c.name);
    if (!bt.length) continue;
    const bset = new Set(bt), bStr = bt.join(" ");
    let score;
    if (aStr === bStr) score = 100;
    else if (aStr.includes(bStr) || bStr.includes(aStr)) score = 85;
    else {
      const inter = [...aset].filter((x) => bset.has(x)).length;
      const uni = new Set([...aset, ...bset]).size;
      score = uni ? Math.round((inter / uni) * 100) : 0;
    }
    if (score > bestScore) { bestScore = score; best = c; }
  }
  return bestScore >= 30 ? best : null;
};

const CatCard = ({ ck, cat, r, isSeries, upd, serverCats }) => {
  const mapped = r.map && r.map !== "none";
  const suggestion = suggestCat(cat.name, serverCats);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`cat-${ck}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-display font-bold text-slate-800">{cat.name}</span>
          <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">{cat.count} itens</span>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Ordem
          <Input data-testid={`ord-${ck}`} value={r.order} onChange={(e) => upd(ck, { order: e.target.value })} className="h-8 w-20 text-center text-xs" />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        <ToggleField label="Importar?" on={r.imp} onToggle={() => upd(ck, { imp: !r.imp })} testid={`imp-${ck}`} />
        <ToggleField label="Criar Categoria?" on={r.create} onToggle={() => upd(ck, { create: !r.create })} testid={`create-${ck}`} />
        <ToggleField label="Ler TMDB?" on={r.tmdb} onToggle={() => upd(ck, { tmdb: !r.tmdb })} testid={`tmdb-${ck}`} />
        {isSeries && <ToggleField label="Separar por Plataforma?" on={r.split} onToggle={() => upd(ck, { split: !r.split })} testid={`split-${ck}`} />}
        <ToggleField label="🔞 Adulto?" on={r.adult} onToggle={() => upd(ck, { adult: !r.adult })} testid={`adult-${ck}`} />
      </div>
      {r.adult && (
        <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-700" data-testid={`adult-note-${ck}`}>
          <Info className="mt-0.5 h-3 w-3 shrink-0" />
          Esta categoria será criada/marcada como <b className="mx-1">ADULTA</b> no seu XUI (is_adult) e o conteúdo vai apenas para os bouquets adultos.
        </div>
      )}

      <div className="mt-3">
        <label className="text-xs font-medium text-slate-500">Categoria no seu servidor</label>
        <select data-testid={`map-${ck}`} value={r.map || "none"} onChange={(e) => upd(ck, { map: e.target.value })}
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-green-500 focus:outline-none">
          <option value="none">-- Não Mapear --</option>
          {(serverCats || []).map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
        </select>
        {suggestion && !mapped && (
          <button type="button" data-testid={`accept-${ck}`} onClick={() => upd(ck, { map: suggestion.id })}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-green-400 bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100">
            <Sparkles className="h-3.5 w-3.5" /> Aceitar “{suggestion.name}”
          </button>
        )}
      </div>

      <div className="mt-2">
        <Input data-testid={`newname-${ck}`} value={r.newName || ""} disabled={mapped}
          onChange={(e) => upd(ck, { newName: e.target.value })} placeholder="Nome da nova categoria (opcional)"
          className={`text-sm ${mapped ? "cursor-not-allowed bg-slate-100 text-slate-400" : ""}`} />
        <p className="mt-1 text-xs text-slate-400">
          {mapped ? "Mapeando para uma categoria existente do servidor."
            : (r.newName ? "Criará uma nova categoria com este nome."
              : "Sem mapear e sem nome: o conteúdo vai para a categoria DIVERSOS.")}
        </p>
      </div>

      <button type="button" data-testid={`excl-${ck}`} onClick={() => upd(ck, { exclude: !r.exclude })}
        className="mt-3 flex items-center gap-2 text-xs text-red-600">
        <span className={`flex h-4 w-4 items-center justify-center rounded border-2 ${r.exclude ? "border-red-600 bg-red-600 text-white" : "border-slate-300"}`}>
          {r.exclude && <Check className="h-3 w-3" />}
        </span>
        Excluir todo o conteúdo desta categoria {isSeries ? "(séries)" : "(filmes)"}
      </button>
    </div>
  );
};

export default function Step4Categories({ code, data, onBack, onNext }) {
  const t = useT();
  const cc = data?.category_config || {};
  const [tmdbLang, setTmdbLang] = useState(cc.tmdb_language || "pt-BR");
  const [movieOpts, setMovieOpts] = useState(cc.movies?.options || { legendado: false, uhd_4k: false, remove_year: false });
  const [prefix, setPrefix] = useState(cc.series?.prefix || "");
  const [uppercase, setUppercase] = useState(!!cc.series?.uppercase);
  const [seriesRemoveYear, setSeriesRemoveYear] = useState(!!cc.series?.remove_year);
  const [scope, setScope] = useState(cc.series?.scope || "all");

  const [cats, setCats] = useState({ movies: [], series: [] });
  const [serverCats, setServerCats] = useState({ movies: [], series: [] });
  const [rows, setRows] = useState({});
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [err, setErr] = useState(false);
  const [errDetail, setErrDetail] = useState("");
  const [serverErr, setServerErr] = useState("");
  const catPoll = useRef(null);

  const loadCats = (refresh = 0) => {
    setErr(false);
    if (refresh) setParsing(true);
    axios.get(`${API}/panel/${code}/m3u-categories${refresh ? "?refresh=1" : ""}`, { timeout: 120000 })
      .then(({ data: d }) => {
        setCats({ movies: d.movies || [], series: d.series || [] });
        setServerCats(d.server_categories || { movies: [], series: [] });
        setSource(d.source);
        setParsing(!!d.parsing);
        setErr(!!d.error);
        setErrDetail(d.error_detail || "");
        setServerErr(d.server_ok ? "" : (d.server_error || ""));
        const saved = {};
        (cc.movies?.categories || []).forEach((c) => (saved[`m-${c.id}`] = c));
        (cc.series?.categories || []).forEach((c) => (saved[`s-${c.id}`] = c));
        const init = {};
        (d.movies || []).forEach((c) => init[`m-${c.id}`] = saved[`m-${c.id}`] || { imp: true, create: false, tmdb: false, adult: false, order: "999", map: "none", newName: "", exclude: false });
        (d.series || []).forEach((c) => init[`s-${c.id}`] = saved[`s-${c.id}`] || { imp: true, create: false, tmdb: false, split: false, adult: false, order: "999", map: "none", newName: "", exclude: false });
        setRows(init);
        // Se está processando em segundo plano, recarrega sozinho até vir o cache.
        if (d.parsing) {
          clearTimeout(catPoll.current);
          catPoll.current = setTimeout(() => loadCats(0), 4000);
        }
      })
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCats(0);
    return () => clearTimeout(catPoll.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const upd = (ck, patch) => setRows((r) => ({ ...r, [ck]: { ...r[ck], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/panel/${code}/category-config`, {
        tmdb_language: tmdbLang,
        movies: { options: movieOpts, categories: cats.movies.map((c) => ({ id: c.id, name: c.name, ...rows[`m-${c.id}`] })) },
        series: { prefix, uppercase, remove_year: seriesRemoveYear, scope, categories: cats.series.map((c) => ({ id: c.id, name: c.name, ...rows[`s-${c.id}`] })) },
      });
      // Só inicia se não houver uma sincronização já em andamento (não interrompe).
      try {
        const { data: st } = await axios.get(`${API}/panel/${code}/sync/status`);
        if (st.status !== "running") await axios.post(`${API}/panel/${code}/sync/restart`);
      } catch (_) {}
      toast.success("Configurações salvas! Iniciando a sincronização…");
      onNext && onNext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar as configurações.");
    } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="mt-6 space-y-6" data-testid="panel-step4">

      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span><b>ATENÇÃO:</b> se for usar a função <b>TMDB</b>, exclua TODO o conteúdo antes (ou use "Excluir tudo" na Etapa 2) para evitar duplicidade. O TMDB não garante 100% de acerto.</span>
      </div>

      <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-xs text-sky-800">
        <div className="flex items-center gap-1.5 font-semibold"><Info className="h-4 w-4" /> Como funciona</div>
        <ul className="mt-1 space-y-1">
          <li>• <b>Importar?</b> — marque as categorias que deseja importar.</li>
          <li>• <b>Criar Categoria?</b> — cria a categoria no seu servidor (as já existentes não são alteradas).</li>
          <li>• <b>Categoria no seu servidor</b> — mapeia os itens dessa categoria para uma categoria do seu servidor.</li>
          <li>• Se não existir, uma categoria <b>DIVERSOS</b> é criada para o que não tiver categoria.</li>
        </ul>
      </div>

      {serverErr && (
        <div data-testid="server-error" className="flex items-start gap-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <b>Não foi possível ler as categorias/bouquets do SEU servidor.</b> Mostrando exemplos abaixo.
            <span className="mt-1 block font-mono text-[11px] text-amber-600">{serverErr}</span>
            <span className="mt-1 block text-[12px]">Se aparecer "Access denied ... @IP", libere no MySQL do seu servidor o acesso desse IP (ou use host <b>%</b>) e o firewall na porta 3306.</span>
          </div>
        </div>
      )}


      {/* TMDB */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-cyan-500 px-5 py-3 font-display text-lg font-bold text-white">
          <Languages className="h-5 w-5" /> {t("steps.s4tmdb")}
        </div>
        <div className="p-5">
          <label className="text-sm font-medium text-slate-700">Idioma preferido do TMDB</label>
          <Input data-testid="tmdb-language" value={tmdbLang} onChange={(e) => setTmdbLang(e.target.value)}
            placeholder="pt-BR" className="mt-1.5 max-w-xs" />
          <p className="mt-1 text-xs text-slate-400">Ex: pt-BR, en-US, es-ES. Se ficar vazio, usamos pt-BR.</p>
        </div>
      </section>

      {source && (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-xs ${(source === "m3u" || source === "cache") ? "border-green-200 bg-green-50 text-green-700" : source === "parsing" ? "border-blue-200 bg-blue-50 text-blue-700" : "border-amber-200 bg-amber-50 text-amber-700"}`} data-testid="cats-source">
          <Server className="h-4 w-4" />
          {(source === "m3u" || source === "cache")
            ? "Categorias lidas da sua lista M3U."
            : source === "parsing"
              ? "Lendo as categorias da sua lista M3U…"
              : "Mostrando categorias de exemplo — salve a Etapa 3 com uma URL M3U válida para ler as reais."}
        </div>
      )}

      {/* FILMES */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-amber-500 px-5 py-3 font-display text-lg font-bold text-white">
          <Film className="h-5 w-5" /> {t("steps.s4movies")}
        </div>
        <div className="space-y-4 p-5">
          <div className="space-y-2">
            {MOVIE_FLAGS.map((o) => (
              <CheckOption key={o.key} opt={o} checked={!!movieOpts[o.key]} testid={`movie-flag-${o.key}`}
                onToggle={() => setMovieOpts({ ...movieOpts, [o.key]: !movieOpts[o.key] })} />
            ))}
          </div>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><ListTree className="h-4 w-4 text-amber-500" /> Categorias de Filmes</div>
            <button type="button" data-testid="refresh-cats" onClick={() => loadCats(1)} disabled={parsing}
              className="flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50">
              <RotateCw className={`h-3.5 w-3.5 ${parsing ? "animate-spin" : ""}`} /> Atualizar categorias
            </button>
          </div>
          {err ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="cats-error">
              <div className="flex-1 min-w-[200px]">
                Não foi possível ler as categorias da sua lista M3U agora.
                {errDetail && <span className="mt-1 block font-mono text-[11px] text-rose-500">Detalhe: {errDetail}</span>}
              </div>
              <button className="rounded-md bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white" onClick={() => loadCats(1)}>Tentar novamente</button>
            </div>
          ) : (loading || (parsing && !cats.movies.length)) ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin text-green-600" /> Lendo categorias da lista M3U… (listas grandes podem levar até ~1 min)</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {cats.movies.map((c) => <CatCard key={`m-${c.id}`} ck={`m-${c.id}`} cat={c} r={rows[`m-${c.id}`] || {}} serverCats={serverCats.movies} upd={upd} />)}
            </div>
          )}
        </div>
      </section>

      {/* SÉRIES */}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 bg-slate-800 px-5 py-3 font-display text-lg font-bold text-white">
          <Clapperboard className="h-5 w-5" /> {t("steps.s4series")}
        </div>
        <div className="space-y-4 p-5">
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            ATENÇÃO: se as categorias já têm a palavra <b>Séries</b> no nome, não digite o prefixo abaixo.
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Prefixo para as categorias de séries criadas (opcional)</label>
            <Input data-testid="series-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)}
              placeholder="Sugestão: Séries |" className="mt-1.5" />
          </div>
          <CheckOption opt={{ label: "Criar categorias com nomes todos em maiúsculo", desc: "Ex: NETFLIX em vez de Netflix." }}
            checked={uppercase} onToggle={() => setUppercase(!uppercase)} testid="series-uppercase" />
          <CheckOption opt={{ label: "Remover ano do título da série", desc: "Remove o ano que aparece junto ao nome da série." }}
            checked={seriesRemoveYear} onToggle={() => setSeriesRemoveYear(!seriesRemoveYear)} testid="series-remove-year" />

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="mb-2 text-sm font-medium text-slate-700">Selecionar quais séries</p>
            {[["all", "Sincronizar todas as séries"], ["some", "Sincronizar somente as categorias marcadas em Importar"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`series-scope-${v}`} onClick={() => setScope(v)}
                className="flex w-full items-center gap-2 py-1.5 text-left text-sm text-slate-700">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${scope === v ? "border-green-600 bg-green-600" : "border-slate-300"}`}>
                  {scope === v && <span className="h-2 w-2 rounded-full bg-white" />}
                </span>
                {l}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><ListTree className="h-4 w-4 text-slate-500" /> Categorias de Séries</div>
          {err ? (
            <div className="flex flex-wrap items-center gap-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <div className="flex-1 min-w-[200px]">
                Não foi possível ler as categorias da sua lista M3U agora.
                {errDetail && <span className="mt-1 block font-mono text-[11px] text-rose-500">Detalhe: {errDetail}</span>}
              </div>
              <button className="rounded-md bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white" onClick={() => loadCats(1)}>Tentar novamente</button>
            </div>
          ) : (loading || (parsing && !cats.series.length)) ? (
            <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin text-green-600" /> Lendo categorias da lista M3U…</div>
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {cats.series.map((c) => <CatCard key={`s-${c.id}`} ck={`s-${c.id}`} cat={c} r={rows[`s-${c.id}`] || {}} isSeries serverCats={serverCats.series} upd={upd} />)}
            </div>
          )}
        </div>
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
        <Button variant="outline" data-testid="step4-back" onClick={onBack}><ArrowLeft className="mr-2 h-4 w-4" /> {t("common.back")}</Button>
        <Button data-testid="step4-save" onClick={save} disabled={saving || loading}
          className="bg-green-600 py-6 text-base font-semibold text-white hover:bg-green-700 sm:py-2">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {t("common.saveStartSync")} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
}
